import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { DesignFirmType, TeamsInterface } from 'ts-interfaces';
import { setSharingV2Flag, sharingV2Default, sharingV2Enabled, sharingV2Known, useSharingV2Store } from './workspace/sharingV2';

// In-memory cache (module-scoped)
const firmCache = new Map(); // key: id, value: firmData

/**
 * Drop the cached firm so the next mount of `useDesignFirm` waits for a
 * fresh Firestore snapshot. Used by team subscription mutation flows
 * (seat change, cancel, resume).
 */
export const invalidateDesignFirmCache = (firmId?: string | null) => {
  if (firmId) firmCache.delete(firmId);
};

// Team billing (plan, card, invoices) comes from the admin-only Express
// team billing API (creator team/hooks/useTeamBilling.ts), not from here.
export const useDesignFirm = (db, id) => {
  const [firm, setFirm] = useState<DesignFirmType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const deactivateTeamMember = useCallback(async (member: TeamsInterface) => {
    if (!db || !id) {
      console.error('Cannot deactivate team member: missing db or design firm ID');
      return { success: false, error: 'Missing db or design firm ID' };
    }
    try {
      const docRef = doc(db, 'teams', member.id);
      await updateDoc(docRef, { status: 'inactive' });
      return { success: true };
    } catch (err) {
      console.error('Error deactivating team member:', err);
      return { success: false, error: err };
    }
  });

  // Live-subscribe to the firm doc. Fields the backend webhook mirrors
  // asynchronously — most notably `seats` after a seat change, and `status`
  // / `stripeSubscriptionId` on cancel — then propagate to every consumer
  // (the team-header seat chip, billing, the Bar, etc.) the moment the
  // webhook lands, with no manual refresh. (Previously a one-shot getDoc, so
  // the seat chip kept showing the old count until the page was reloaded.)
  useEffect(() => {
    if (!db || !id) {
      setFirm(null);
      setLoading(false);
      return;
    }

    // Seed synchronously from the module cache to avoid a load flash when
    // this firm was already viewed earlier in the session.
    const cached: DesignFirmType | undefined = firmCache.get(id);
    if (cached) {
      setFirm(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);

    const unsub = onSnapshot(
      doc(db, 'design_firms', id),
      (snap) => {
        if (!snap.exists()) {
          firmCache.delete(id);
          setFirm(null);
          setLoading(false);
          return;
        }
        const firmData = { id: snap.id, ...snap.data() } as DesignFirmType;
        firmCache.set(id, firmData);
        setSharingV2Flag(id, firmData.sharingV2 === true);
        setFirm(firmData);
        setLoading(false);
      },
      (err) => {
        console.error('useDesignFirm firm snapshot error:', err);
        // Clear cached/seeded firm so stale team metadata isn't left visible
        // after a permission/access failure (e.g. losing team access mid-session).
        firmCache.delete(id);
        setFirm(null);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [db, id]);

  // New function to save settings to backend
  const saveSettings = useCallback(async (settings) => {
    if (!db || !id) {
      console.error('Cannot save settings: missing db or design firm ID');
      return { success: false, error: 'Missing db or design firm ID' };
    }

    setSavingSettings(true);
    console.log(`Saving settings for design firm ${id}:`, settings);

    try {
      // Update Firestore document
      const docRef = doc(db, 'design_firms', id);
      await updateDoc(docRef, { ...settings });

      // Update local cache
      if (firmCache.has(id)) {
        const cachedFirm = firmCache.get(id);
        firmCache.set(id, { ...cachedFirm, ...settings });
      }

      // Update state
      setFirm(prevFirm => {
        if (!prevFirm) return null;
        return { ...prevFirm, ...settings };
      });

      setSavingSettings(false);
      return { success: true };
    } catch (err) {
      console.error('Error saving settings:', err);
      setSavingSettings(false);
      setError(err);
      return { success: false, error: err };
    }
  }, [db, id]);

  return { firm, loading, error, saveSettings, savingSettings, deactivateTeamMember };
};

// The team's sharingV2 flag, live. Mounting this keeps the firm doc subscribed
// so the sync `sharingV2Enabled(firmId)` reads stay current for the stores.
export const useSharingV2 = (db, firmId: string | null | undefined) => {
  const { loading } = useDesignFirm(db, firmId || null);
  const flag = useSharingV2Store((s) => (firmId ? s.flags[firmId] : undefined));
  const enabled = useMemo(() => sharingV2Enabled(firmId), [firmId, flag]);
  return { enabled, loading: !!firmId && loading && !sharingV2Default() };
};

// Loads a team's flag once, for a doc opened by link before any mounted
// useDesignFirm has (cold load, or a team from another workspace), so its
// access check uses the right rule. On error the legacy check applies and the
// next call reads again.
const flagReads = new Map<string, Promise<void>>();
export const ensureSharingV2Flag = (db, firmId: string | null | undefined): Promise<void> => {
  if (!db || !firmId || sharingV2Known(firmId)) return Promise.resolve();
  let read = flagReads.get(firmId);
  if (!read) {
    read = Promise.resolve()
      .then(() => getDoc(doc(db, 'design_firms', firmId)))
      .then((snap) => setSharingV2Flag(firmId, snap.data()?.sharingV2 === true))
      .catch(() => {
        flagReads.delete(firmId);
      });
    flagReads.set(firmId, read);
  }
  return read;
};

export default useDesignFirm;
